import type { FieldErrors } from '../api/errors';
import { normalizeControlType } from '../api/normalize/controlType';
import type { CustomField } from '../api/types';
import { fieldOptions, type CustomFieldValue } from '../domain/customFieldValues';
import { stripRequiredMarker } from '../domain/labels';
import { htmlToText } from '../domain/sanitize';
import { customFieldKey } from '../domain/validation';
import { shouldShowError } from '../state/selectors';
import { useEnv, useFormState } from './context';
import { Field } from './fields/Field';
import { SelectField, TextField, TextareaField } from './fields/inputs';
import { fieldId } from './format';
import { Html } from './Html';
import { CheckIcon } from './icons';

interface Props {
  fields: CustomField[];
  errors: FieldErrors;
}

const INPUT_TYPE: Record<string, string> = { text: 'text', number: 'number', date: 'date', datetime: 'datetime-local' };

export function CustomFields({ fields, errors }: Props) {
  const { controller } = useEnv();
  const state = useFormState();
  if (!fields.length) return null;

  const show = (key: string) => (shouldShowError(state, key, 2) ? Array.from(new Set([...(errors[key] ?? []), ...(state.apiErrors[key] ?? [])])) : []);

  return (
    <>
      {fields.map((field) => {
        if (typeof field.id !== 'number') return null;
        const id = field.id;
        const key = customFieldKey(id);
        const { type, known } = normalizeControlType(field.control_type);
        if (!known) console.warn(`[cbf] unknown custom field control_type "${field.control_type}" (id ${id}); rendering as text`);
        const value = state.values.customFields[id];
        const send = (next: CustomFieldValue) => controller.send({ type: 'CUSTOM_FIELD_CHANGED', fieldId: id, value: next });
        const blur = () => controller.send({ type: 'FIELD_BLURRED', key });
        const labelText = stripRequiredMarker(htmlToText(field.label ?? '')).text;
        const required = field.value_required === true;
        const options = fieldOptions(field);
        const otherInput = (optionId: number, current: Record<number, string>, update: (other: Record<number, string>) => void) => (
          <TextField
            key={`other-${optionId}`}
            fieldKey={`${key}.other.${optionId}`}
            label="Please specify"
            value={current[optionId] ?? ''}
            onChange={(v) => update({ ...current, [optionId]: v })}
            onBlur={blur}
          />
        );

        switch (type) {
          case 'static':
            return <Html key={id} as="div" className="cbf-static" html={field.text ?? ''} />;

          case 'textarea':
            return (
              <TextareaField key={id} fieldKey={key} label={labelText} required={required} errors={show(key)} placeholder={field.help_text ?? undefined} value={value?.kind === 'text' ? value.value : ''} onChange={(v) => send({ kind: 'text', value: v })} onBlur={blur} />
            );

          case 'text':
          case 'number':
          case 'date':
          case 'datetime':
            return (
              <TextField key={id} fieldKey={key} type={INPUT_TYPE[type]} label={labelText} required={required} errors={show(key)} placeholder={type === 'text' ? field.help_text ?? undefined : undefined} help={type !== 'text' ? field.help_text : null} value={value?.kind === 'text' ? value.value : ''} onChange={(v) => send({ kind: 'text', value: v })} onBlur={blur} />
            );

          case 'select': {
            const current = value?.kind === 'single' ? value : { kind: 'single' as const, optionId: null, other: {} };
            const other = options.find((o) => o.id === current.optionId && o.other === true);
            return (
              <div key={id} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <SelectField fieldKey={key} label={labelText} required={required} errors={show(key)} help={field.help_text} value={current.optionId === null ? '' : String(current.optionId)} options={options.map((o) => ({ value: String(o.id), label: o.label ?? '' }))} onChange={(v) => send({ ...current, optionId: v ? Number(v) : null })} onBlur={blur} />
                {other ? otherInput(other.id!, current.other, (o) => send({ ...current, other: o })) : null}
              </div>
            );
          }

          case 'radio': {
            const current = value?.kind === 'single' ? value : { kind: 'single' as const, optionId: null, other: {} };
            const other = options.find((o) => o.id === current.optionId && o.other === true);
            return (
              <div key={id} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Field fieldKey={key} label={labelText} required={required} errors={show(key)} help={field.help_text} group>
                  {() => (
                    <div className="cbf-choice-grid" role="radiogroup">
                      {options.map((o) => {
                        const on = current.optionId === o.id;
                        const oid = `${fieldId(key)}-${o.id}`;
                        return (
                          <label key={o.id} className={`cbf-choice${on ? ' is-on' : ''}`} htmlFor={oid}>
                            <input id={oid} type="radio" name={fieldId(key)} checked={on} onChange={() => send({ ...current, optionId: o.id! })} onBlur={blur} />
                            <span className="cbf-radio" aria-hidden="true" />
                            <span className="cbf-choice-body">
                              <span className="cbf-choice-title">
                                <span>{o.label}</span>
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </Field>
                {other ? otherInput(other.id!, current.other, (o) => send({ ...current, other: o })) : null}
              </div>
            );
          }

          case 'multiselect': {
            const current = value?.kind === 'multi' ? value : { kind: 'multi' as const, optionIds: [], other: {} };
            // A checkboxes field with a single non-"other" option is really one yes/no toggle
            // (the client's Point-of-Contact field) — render it like a single checkbox.
            if (options.length === 1 && options[0]!.other !== true) {
              const only = options[0]!;
              const checked = current.optionIds.includes(only.id!);
              const errs = show(key);
              const inputId = fieldId(key);
              return (
                <div key={id} className="cbf-field" data-cbf-field={key}>
                  <label className={`cbf-checkrow${checked ? ' is-on' : ''}`} htmlFor={inputId}>
                    <input id={inputId} type="checkbox" checked={checked} aria-invalid={errs.length > 0 || undefined} aria-describedby={errs.length ? `${inputId}-error` : undefined} aria-required={required || undefined} onChange={(e) => send({ ...current, optionIds: e.target.checked ? [only.id!] : [] })} onBlur={blur} />
                    <span className="cbf-check" aria-hidden="true">{checked ? <CheckIcon /> : null}</span>
                    <span className="cbf-checkrow-label">
                      <Html html={field.label ?? ''} />
                      {required ? <span className="cbf-req" aria-hidden="true"> *</span> : null}
                      {required ? <span className="cbf-sr"> (required)</span> : null}
                    </span>
                  </label>
                  {errs.length ? (
                    <p className="cbf-error" id={`${inputId}-error`} role="alert">
                      {errs.join(' ')}
                    </p>
                  ) : field.help_text ? (
                    <p className="cbf-help">{field.help_text}</p>
                  ) : null}
                </div>
              );
            }
            const others = options.filter((o) => o.other === true && current.optionIds.includes(o.id!));
            return (
              <div key={id} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Field fieldKey={key} label={labelText} required={required} errors={show(key)} help={field.help_text} group>
                  {() => (
                    <div className="cbf-choice-grid">
                      {options.map((o) => {
                        const on = current.optionIds.includes(o.id!);
                        const oid = `${fieldId(key)}-${o.id}`;
                        return (
                          <label key={o.id} className={`cbf-choice${on ? ' is-on' : ''}`} htmlFor={oid}>
                            <input id={oid} type="checkbox" checked={on} onChange={(e) => send({ ...current, optionIds: e.target.checked ? [...current.optionIds, o.id!] : current.optionIds.filter((x) => x !== o.id) })} onBlur={blur} />
                            <span className="cbf-check" aria-hidden="true">{on ? <CheckIcon /> : null}</span>
                            <span className="cbf-choice-body">
                              <span className="cbf-choice-title">
                                <span>{o.label}</span>
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </Field>
                {others.map((o) => otherInput(o.id!, current.other, (oth) => send({ ...current, other: oth })))}
              </div>
            );
          }

          case 'checkbox': {
            const checked = value?.kind === 'checkbox' ? value.checked : false;
            const errs = show(key);
            const inputId = fieldId(key);
            return (
              <div key={id} className="cbf-field" data-cbf-field={key}>
                <label className={`cbf-checkrow${checked ? ' is-on' : ''}`} htmlFor={inputId}>
                  <input id={inputId} type="checkbox" checked={checked} aria-invalid={errs.length > 0 || undefined} aria-describedby={errs.length ? `${inputId}-error` : undefined} aria-required={required || undefined} onChange={(e) => send({ kind: 'checkbox', checked: e.target.checked })} onBlur={blur} />
                  <span className="cbf-check" aria-hidden="true">{checked ? <CheckIcon /> : null}</span>
                  <span className="cbf-checkrow-label">
                    <Html html={field.label ?? ''} />
                    {required ? <span className="cbf-req" aria-hidden="true"> *</span> : null}
                    {required ? <span className="cbf-sr"> (required)</span> : null}
                  </span>
                </label>
                {errs.length ? (
                  <p className="cbf-error" id={`${inputId}-error`} role="alert">
                    {errs.join(' ')}
                  </p>
                ) : field.help_text ? (
                  <p className="cbf-help">{field.help_text}</p>
                ) : null}
              </div>
            );
          }
        }
      })}
    </>
  );
}
